import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import BrandLogo from '../../components/BrandLogo';
import { useAuth, ONBOARDING_DRAFT_PREFIX } from '../../contexts/AuthContext';
import {
  WriterProfileData,
  emptyWriterProfile,
  fromResponse,
} from '../../features/writerProfile/types';
import { completeWriterProfile, getWriterProfile } from '../../features/writerProfile/api';
import {
  FormatsSection,
  GenresSection,
  InfluencesSection,
  StyleSection,
  GoalSection,
} from '../../features/writerProfile/components';

const TOTAL_STEPS = 5;

// Draft is keyed by user so it can never pre-fill another account's wizard on a
// shared browser; AuthContext.logout() also clears every draft.
const draftKey = (userId: string) => `${ONBOARDING_DRAFT_PREFIX}${userId}`;

const readDraft = (userId: string): WriterProfileData | null => {
  try {
    const raw = sessionStorage.getItem(draftKey(userId));
    return raw ? { ...emptyWriterProfile(), ...JSON.parse(raw) } : null;
  } catch {
    return null;
  }
};

const Onboarding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { step: stepParam } = useParams();
  const { user, markOnboarded } = useAuth();

  const step = useMemo(() => {
    const n = parseInt(stepParam || '1', 10);
    return Number.isFinite(n) && n >= 1 && n <= TOTAL_STEPS ? n : 1;
  }, [stepParam]);

  const userId = user?.id ?? '';
  const [data, setData] = useState<WriterProfileData>(
    () => readDraft(userId) ?? emptyWriterProfile(),
  );
  const [loaded, setLoaded] = useState(!!readDraft(userId));
  const [submitting, setSubmitting] = useState(false);

  // Seed from a previously saved (incomplete) profile the first time we open the wizard.
  useEffect(() => {
    if (loaded) return;
    let cancelled = false;
    getWriterProfile()
      .then((profile) => {
        if (!cancelled && profile) setData(fromResponse(profile));
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [loaded]);

  useEffect(() => {
    // Don't persist the empty initial state before the saved profile has been seeded,
    // or a quick remount would treat that empty draft as the writer's choices.
    if (!userId || !loaded) return;
    try {
      sessionStorage.setItem(draftKey(userId), JSON.stringify(data));
    } catch {}
  }, [data, userId, loaded]);

  const patch = (p: Partial<WriterProfileData>) => setData((d) => ({ ...d, ...p }));

  const goTo = (n: number) => navigate(`/onboarding/${n}`);

  const finish = async (skipped = false) => {
    setSubmitting(true);
    try {
      await completeWriterProfile(data);
      try {
        sessionStorage.removeItem(draftKey(userId));
      } catch {}
      markOnboarded();
      toast.success(
        skipped
          ? t('onboarding.skippedToast', 'You can set up your writer profile anytime in Settings.')
          : t('onboarding.doneToast', 'Your writing studio is ready.'),
      );
      navigate('/stories', { replace: true });
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          t('onboarding.saveError', 'Could not save your profile. Please try again.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const titles: Record<number, { title: string; subtitle: string }> = {
    1: {
      title: t('onboarding.step1Title', 'Welcome, {{name}}', {
        name: user?.name?.split(' ')[0] || t('onboarding.writer', 'writer'),
      }),
      subtitle: t(
        'onboarding.step1Subtitle',
        'Tell us how you write so the AI can write with you.',
      ),
    },
    2: {
      title: t('onboarding.step2Title', 'What do you love to read?'),
      subtitle: t('onboarding.step2Subtitle', 'Genres shape rhythm, stakes, and tone.'),
    },
    3: {
      title: t('onboarding.step3Title', 'Who shaped your taste?'),
      subtitle: t(
        'onboarding.step3Subtitle',
        'Books and influences give the AI a compass, not a copy.',
      ),
    },
    4: {
      title: t('onboarding.step4Title', 'How do you sound?'),
      subtitle: t(
        'onboarding.step4Subtitle',
        'A few leanings and a sentence or two in your words.',
      ),
    },
    5: {
      title: t('onboarding.step5Title', 'What are you here to do?'),
      subtitle: t('onboarding.step5Subtitle', 'We’ll tune suggestions to your goal.'),
    },
  };

  const section = {
    1: <FormatsSection data={data} onChange={patch} />,
    2: <GenresSection data={data} onChange={patch} />,
    3: <InfluencesSection data={data} onChange={patch} />,
    4: <StyleSection data={data} onChange={patch} />,
    5: <GoalSection data={data} onChange={patch} />,
  }[step];

  const isLast = step === TOTAL_STEPS;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: 'var(--ds-bg-primary, var(--color-light-bg))' }}
    >
      <div className="flex-1 flex flex-col max-w-mobile md:max-w-desktop mx-auto w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-14">
          <div className="w-10">
            {step > 1 && (
              <button
                type="button"
                onClick={() => goTo(step - 1)}
                aria-label={t('common.back', 'Back')}
                className="w-10 h-10 flex items-center justify-center rounded-full"
              >
                <ArrowLeftIcon className="w-5 h-5" style={{ color: 'var(--ds-text-primary)' }} />
              </button>
            )}
          </div>
          <BrandLogo
            imgClassName="h-6 w-auto"
            textClassName="text-lg font-semibold font-cormorant"
          />
          <button
            type="button"
            onClick={() => finish(true)}
            disabled={submitting}
            className="text-sm font-medium text-primary-500 disabled:opacity-50"
          >
            {t('onboarding.skip', 'Skip')}
          </button>
        </div>

        {/* Progress */}
        <div
          className="px-6 pt-2 pb-4"
          aria-label={t('onboarding.progress', 'Step {{step}} of {{total}}', {
            step,
            total: TOTAL_STEPS,
          })}
        >
          <div className="flex gap-1.5">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => (
              <div
                key={i}
                className="h-1.5 flex-1 rounded-full transition-colors"
                style={{ background: i < step ? 'var(--ds-accent, #a855f7)' : 'var(--ds-border)' }}
              />
            ))}
          </div>
          <div className="text-xs mt-2" style={{ color: 'var(--ds-text-secondary)' }}>
            {t('onboarding.progress', 'Step {{step}} of {{total}}', { step, total: TOTAL_STEPS })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <h1
            className="font-cormorant text-3xl font-bold mb-1"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {titles[step].title}
          </h1>
          <p className="mb-6" style={{ color: 'var(--ds-text-secondary)' }}>
            {titles[step].subtitle}
          </p>
          {section}
        </div>

        {/* Footer */}
        <div className="px-6 pb-8 pt-2">
          {isLast ? (
            <button
              type="button"
              onClick={() => finish(false)}
              disabled={submitting}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 text-white font-semibold rounded-xl transition-colors"
            >
              {submitting
                ? t('onboarding.saving', 'Setting up your studio…')
                : t('onboarding.finish', 'Start writing')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => goTo(step + 1)}
              className="w-full py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-xl transition-colors"
            >
              {t('onboarding.next', 'Continue')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
